import { useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, errMsg, API_BASE } from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";
import { isTerminalStatus, ASSIGNED_STATUSES } from "@/lib/status";
import { formatTimeAgo, formatPrice } from "@/lib/utils-app";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { MapPin, Zap, Star, Clock, Loader2, MessageCircle } from "lucide-react";

export function RequestDetail() {
  const { requestId } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [answer, setAnswer] = useState("");
  const [busy, setBusy] = useState(false);

  const { data: request, isLoading } = useQuery({
    queryKey: ["request", requestId],
    queryFn: async () => (await api.get(`/requests/${requestId}`)).data,
  });

  const { data: offers } = useQuery({
    queryKey: ["offers", requestId],
    queryFn: async () => (await api.get(`/requests/${requestId}/offers`)).data,
  });

  const accept = async (offerId) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/offers/${offerId}/accept`);
      toast.success("Offerta accettata!");
      const jobId = data.job_id || data.job?.id || data.id;
      if (jobId) navigate(`/lavori/${jobId}`);
      else qc.invalidateQueries({ queryKey: ["request", requestId] });
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  const clarify = async () => {
    if (!answer.trim()) return;
    setBusy(true);
    try {
      await api.post(`/requests/${requestId}/clarify`, { answer });
      toast.success("Risposta inviata");
      setAnswer("");
      qc.invalidateQueries({ queryKey: ["request", requestId] });
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  const cancel = async () => {
    setBusy(true);
    try {
      await api.post(`/requests/${requestId}/cancel`);
      toast.success("Richiesta annullata");
      qc.invalidateQueries({ queryKey: ["request", requestId] });
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  if (isLoading) return <div className="px-4 pt-6 space-y-3"><Skeleton className="h-40 rounded-3xl" /><Skeleton className="h-24 rounded-3xl" /></div>;
  if (!request) return <div className="px-4 pt-6 text-sm text-slate-500" data-testid="request-not-found">Richiesta non trovata.</div>;

  const needsClarify = request.clarification_question || ["domande", "needs_clarification"].includes(request.status);
  const cancellable = !isTerminalStatus(request.status) && !ASSIGNED_STATUSES.includes(request.status);

  return (
    <div className="px-4 sm:px-6 pt-6 sm:pt-10 space-y-5 sm:max-w-2xl sm:mx-auto" data-testid="request-detail-page">
      <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-5 space-y-3">
        <div className="flex items-start justify-between gap-2">
          <h1 className="font-heading text-xl font-semibold text-slate-900 leading-snug" data-testid="request-title">{request.title || request.raw_text}</h1>
          <StatusBadge status={request.status} testid="request-detail-status" />
        </div>
        <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500">
          <span className="capitalize font-semibold text-slate-700">{request.category}</span>
          <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{request.zone || request.address || "—"}</span>
          <span>{formatTimeAgo(request.created_at)}</span>
          {request.urgent && <span className="text-whoo font-bold flex items-center gap-1"><Zap className="w-3.5 h-3.5" />Urgente</span>}
        </div>
        {request.description && request.description !== request.title && <p className="text-sm text-slate-600 leading-relaxed">{request.description}</p>}
        {request.media?.length > 0 && (
          <div className="flex gap-2 flex-wrap pt-1">
            {request.media.map((m) => (
              <img key={m.id || m} src={`${API_BASE}/api/media/${m.id || m}`} alt="" className="w-20 h-20 rounded-2xl object-cover border border-slate-200" />
            ))}
          </div>
        )}
      </div>

      {needsClarify && (
        <div className="bg-volt-soft rounded-3xl p-4 space-y-3" data-testid="clarify-section">
          <p className="text-sm font-semibold text-slate-900">{request.clarification_question || "Il sistema richiede un chiarimento:"}</p>
          <Textarea data-testid="clarify-input" rows={2} value={answer} onChange={(e) => setAnswer(e.target.value)}
            className="bg-white border-transparent rounded-2xl resize-none" placeholder="Scrivi la tua risposta..." />
          <Button data-testid="clarify-submit-btn" onClick={clarify} disabled={busy} size="sm" className="rounded-full bg-volt hover:bg-volt/90 text-white font-semibold">
            Invia risposta
          </Button>
        </div>
      )}

      <section className="space-y-3">
        <h2 className="font-heading text-lg font-semibold text-slate-900" data-testid="offers-title">
          Offerte ricevute {offers?.length ? `(${offers.length})` : ""}
        </h2>
        {!offers?.length ? (
          <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-6 text-center" data-testid="no-offers">
            <p className="text-sm text-slate-500">Nessuna offerta ancora. I professionisti disponibili vedranno presto la tua richiesta.</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="offers-list">
            {offers.map((o) => (
              <div key={o.id} data-testid={`offer-${o.id}`} className="bg-white rounded-3xl border border-slate-100 shadow-sm p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-10 h-10 rounded-xl bg-volt-soft text-volt flex items-center justify-center font-heading font-bold">
                      {(o.pro_name || o.professional_name || "P")[0].toUpperCase()}
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900 text-sm">{o.pro_name || o.professional_name || "Professionista"}</p>
                      {(o.pro_rating ?? o.rating) > 0 && (
                        <p className="text-xs text-slate-500 flex items-center gap-1"><Star className="w-3 h-3 fill-amber-400 text-amber-400" />{(o.pro_rating ?? o.rating).toFixed(1)}</p>
                      )}
                    </div>
                  </div>
                  <p className="font-heading text-xl font-bold text-slate-900" data-testid={`offer-price-${o.id}`}>{formatPrice(o.price)}</p>
                </div>
                <div className="flex items-center gap-3 text-xs text-slate-500">
                  <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" />Arrivo in ~{o.eta_minutes} min</span>
                  <StatusBadge status={o.status} testid={`offer-status-${o.id}`} />
                </div>
                {o.notes && <p className="text-sm text-slate-600 leading-relaxed">{o.notes}</p>}
                <div className="flex gap-2">
                  {o.status !== "accettata" && cancellable && (
                    <Button data-testid={`accept-offer-${o.id}`} onClick={() => accept(o.id)} disabled={busy}
                      className="flex-1 rounded-full bg-whoo hover:bg-whoo-dark text-white font-semibold active:scale-[0.98] transition-transform">
                      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Accetta offerta"}
                    </Button>
                  )}
                  {(o.conversation_id || o.status === "accettata") && (
                    <Link to={o.conversation_id ? `/chat/${o.conversation_id}` : "/chat"} className="flex-1">
                      <Button data-testid={`chat-offer-${o.id}`} variant="outline" className="w-full rounded-full font-semibold">
                        <MessageCircle className="w-4 h-4 mr-2" /> Chat
                      </Button>
                    </Link>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {cancellable && (
        <Button data-testid="cancel-request-btn" variant="outline" onClick={cancel} disabled={busy}
          className="w-full rounded-full text-red-600 border-red-200 hover:bg-red-50 font-semibold">
          Annulla richiesta
        </Button>
      )}
    </div>
  );
}
